import type { ClientSessionInventory } from '@fitness-force/models';

/** Distinct Tailwind color names used for subscription indicators (matches booking-type palette). */
export const SUBSCRIPTION_COLOR_PALETTE = [
  'blue',
  'green',
  'orange',
  'purple',
  'pink',
  'teal',
  'indigo',
  'cyan',
  'amber',
  'rose',
  'lime',
  'fuchsia',
] as const;

export type SubscriptionColorName = (typeof SUBSCRIPTION_COLOR_PALETTE)[number];

/** Full dot class names (must be static strings for JIT / purge). */
export const SUBSCRIPTION_COLOR_BG_CLASS: Record<
  SubscriptionColorName,
  string
> = {
  blue: 'bg-blue-400',
  green: 'bg-green-400',
  orange: 'bg-orange-400',
  purple: 'bg-purple-400',
  pink: 'bg-pink-400',
  teal: 'bg-teal-400',
  indigo: 'bg-indigo-400',
  cyan: 'bg-cyan-400',
  amber: 'bg-amber-400',
  rose: 'bg-rose-400',
  lime: 'bg-lime-400',
  fuchsia: 'bg-fuchsia-400',
};

export function getSubscriptionColorBgClass(
  color: SubscriptionColorName | null | undefined,
): string {
  return color ? SUBSCRIPTION_COLOR_BG_CLASS[color] : '';
}

export function buildSubscriptionColorMap(
  inventory: ClientSessionInventory[],
): ReadonlyMap<number, SubscriptionColorName> {
  const map = new Map<number, SubscriptionColorName>();
  const assignedSubscriptionIds = new Set<number>();
  let colorIndex = 0;

  for (const item of inventory) {
    const subscriptionId = item.subscription_id;
    if (subscriptionId <= 0 || assignedSubscriptionIds.has(subscriptionId)) {
      continue;
    }
    assignedSubscriptionIds.add(subscriptionId);
    map.set(
      subscriptionId,
      SUBSCRIPTION_COLOR_PALETTE[colorIndex % SUBSCRIPTION_COLOR_PALETTE.length],
    );
    colorIndex++;
  }

  return map;
}

export function getSubscriptionColor(
  map: ReadonlyMap<number, SubscriptionColorName> | null | undefined,
  subscriptionId: number | null | undefined,
): SubscriptionColorName | null {
  if (!map || subscriptionId == null || subscriptionId <= 0) {
    return null;
  }
  return map.get(subscriptionId) ?? null;
}

export function getSubscriptionColorBgClassForId(
  map: ReadonlyMap<number, SubscriptionColorName> | null | undefined,
  subscriptionId: number | null | undefined,
): string {
  return getSubscriptionColorBgClass(getSubscriptionColor(map, subscriptionId));
}
